// src/components/kuestion/KuestionArea.jsx
//
// Everything under the Kuestion mount point (Separation contract v2.1 §5).
// Kuestion has its own door: its own sign-in, its own session namespace
// (rw.kuestion.*), and its own copies of the emailed-link pages. Same origin
// as the apex means the namespacing is a UX separation, not a security
// boundary — signing out here must leave the rest of Riftworks as it was.
import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../../context/AuthContext';
import { StudentAuthProvider, useStudentAuth } from '../../context/StudentAuthContext';
import Login from '../loginpage/Login';
import ResetPassword from '../loginpage/resetpassword/ResetPassword';
import VerificationSuccess from '../loginpage/verificationsuccess/verificationsuccess';
import VerificationProblem from '../loginpage/verificationproblem/VerificationProblem';
import CampusApp from './campus/CampusApp';
import StudentApp, { StudentRoute } from './student/StudentApp';
import KnowledgeBase from './kb/KnowledgeBase';
import { kq } from '../../config/routes';

const Holding = () => (
  <div style={{
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    height: '100vh', background: 'var(--color-bg, #f3f2f2)', color: 'var(--color-text, #201e1d)',
    fontFamily: 'var(--font-body, sans-serif)'
  }}>
    Loading...
  </div>
);

// /kuestion/login — a live session skips the form.
const LoginRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isStudent } = useStudentAuth();
  if (isLoading) return <Holding />;
  if (isAuthenticated) return <Navigate to={kq()} replace />;
  if (isStudent) return <Navigate to={kq('student')} replace />;
  return <Login area="kuestion" />;
};

// /kuestion and everything the app owns beneath it. Signed out, the sign-in
// renders in place: the public address is the door (contract §1).
const AppRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isStudent } = useStudentAuth();
  if (isLoading) return <Holding />;
  if (isAuthenticated) return <CampusApp />;
  if (isStudent) return <Navigate to={kq('student')} replace />;
  return <Login area="kuestion" />;
};

export default function KuestionArea() {
  // The tab says which door this is; the apex title comes back on the way out.
  useEffect(() => {
    const before = document.title;
    document.title = 'Kuestion · Rift Works';
    return () => { document.title = before; };
  }, []);

  return (
    <AuthProvider area="kuestion">
      <StudentAuthProvider>
        <Routes>
          <Route path="login" element={<LoginRoute />} />
          <Route path="reset-password" element={<ResetPassword area="kuestion" />} />
          <Route path="verificationsuccess" element={<VerificationSuccess area="kuestion" />} />
          <Route path="verificationexpired" element={<VerificationProblem kind="expired" area="kuestion" />} />
          <Route path="verificationinvalid" element={<VerificationProblem kind="invalid" area="kuestion" />} />
          <Route path="student/*" element={<StudentRoute><StudentApp /></StudentRoute>} />
          {/* Public: readable signed out, with the session used only to sign feedback */}
          <Route path="kb/*" element={<KnowledgeBase />} />
          <Route path="*" element={<AppRoute />} />
        </Routes>
      </StudentAuthProvider>
    </AuthProvider>
  );
}
